import { ref, toRef, computed, watch, onBeforeUnmount } from 'vue';

// Per-axis mapping of the physical box/scroll properties we read and write, so
// the rest of the logic can stay axis-agnostic.
const AXES = {
  horizontal: {
    start: 'left',
    end: 'right',
    size: 'width',
    scrollPos: 'scrollLeft',
    clientSize: 'clientWidth',
    scrollSize: 'scrollWidth',
    scrollByKey: 'left',
  },
  vertical: {
    start: 'top',
    end: 'bottom',
    size: 'height',
    scrollPos: 'scrollTop',
    clientSize: 'clientHeight',
    scrollSize: 'scrollHeight',
    scrollByKey: 'top',
  },
};

// Track which items in a scrollable list are visible / partially clipped /
// hidden, and expose affordances (edge buttons, fade mask) for the side(s)
// with hidden content. `target` is the element whose direct children are the
// items, given as a ref, a getter, or a raw element. `options` accepts
// { axis: 'horizontal' | 'vertical', epsilon: number }.
export const useScrollAffordance = (target, options = {}) => {
  // Accept a ref, a getter, or a raw element; normalize to a single ref.
  const listRef = toRef(target);
  const axis = AXES[options.axis === 'vertical' ? 'vertical' : 'horizontal'];
  // Edge comparisons use a small tolerance to absorb sub-pixel rounding.
  const epsilon = options.epsilon ?? 1;

  const canScrollStart = ref(false);
  const canScrollEnd = ref(false);
  // Visibility per item, indexed to match the list's children.
  const itemVisibility = ref([]);

  // Which edge(s) have hidden content: start | end | both | none.
  const fadeEdges = computed(() => {
    if (canScrollStart.value && canScrollEnd.value) return 'both';
    if (canScrollStart.value) return 'start';
    if (canScrollEnd.value) return 'end';
    return 'none';
  });

  let resizeObserver = null;
  let mutationObserver = null;
  let scrollTargets = [];
  let rafId = null;
  let measureRafId = null;
  let viewportRect = null;

  // The list and its wrapper may both have `overflow: auto`; resolve whichever
  // one is actually overflowing so we read/scroll a single, consistent element.
  function getScroller() {
    const list = listRef.value;
    if (!list) return null;
    if (list[axis.scrollSize] - list[axis.clientSize] > epsilon) return list;
    const wrapper = list.parentElement;
    if (wrapper && wrapper[axis.scrollSize] - wrapper[axis.clientSize] > epsilon) return wrapper;
    return list;
  }

  // The scroller's own rect only changes on resize/layout (its internal
  // scrolling does not move it), so cache it and refresh on mount/resize/
  // content-change rather than re-measuring on every scroll frame.
  function refreshViewport() {
    const scroller = getScroller();
    viewportRect = scroller ? scroller.getBoundingClientRect() : null;
  }

  // Resolve the scroller and current item elements, or null when not measurable.
  function getScrollState() {
    const list = listRef.value;
    const scroller = getScroller();
    if (!list || !scroller || !viewportRect) return null;
    return { scroller, items: Array.from(list.children) };
  }

  // Recompute per-item visibility and edge state. Button state comes from the
  // live scroll position, which is the source of truth and cannot go stale.
  function update() {
    const state = getScrollState();
    if (!state) return;
    const { scroller, items } = state;

    itemVisibility.value = items.map((item) => {
      const rect = item.getBoundingClientRect();
      const offStart = rect[axis.start] < viewportRect[axis.start] - epsilon;
      const offEnd = rect[axis.end] > viewportRect[axis.end] + epsilon;

      if (rect[axis.end] <= viewportRect[axis.start] || rect[axis.start] >= viewportRect[axis.end]) {
        return 'hidden';
      }
      return offStart || offEnd ? 'partial' : 'visible';
    });

    canScrollStart.value = scroller[axis.scrollPos] > epsilon;
    canScrollEnd.value =
      scroller[axis.scrollPos] + scroller[axis.clientSize] < scroller[axis.scrollSize] - epsilon;
  }

  function refreshAndUpdate() {
    refreshViewport();
    update();
  }

  // Coalesce bursts of scroll events into a single measurement per frame.
  function scheduleUpdate() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      update();
    });
  }

  // Bring the next clipped item on the given side into view. The target is
  // centered (rather than edge-aligned) so the move agrees with any
  // `scroll-snap-align: center` on the items instead of fighting it.
  function scrollToNext(direction) {
    const state = getScrollState();
    if (!state) return;
    const { scroller, items } = state;

    let targetRect = null;

    if (direction === 'start') {
      // Last item still clipped past the start edge.
      for (const item of items) {
        const rect = item.getBoundingClientRect();
        if (rect[axis.start] < viewportRect[axis.start] - epsilon) {
          targetRect = rect;
        } else {
          break;
        }
      }
    } else {
      // First item still clipped past the end edge.
      for (const item of items) {
        const rect = item.getBoundingClientRect();
        if (rect[axis.end] > viewportRect[axis.end] + epsilon) {
          targetRect = rect;
          break;
        }
      }
    }

    if (!targetRect) return;

    const targetCenter = targetRect[axis.start] + targetRect[axis.size] / 2;
    const viewportCenter = viewportRect[axis.start] + viewportRect[axis.size] / 2;
    scroller.scrollBy({ [axis.scrollByKey]: targetCenter - viewportCenter, behavior: 'smooth' });
  }

  function attach(list) {
    // Either the list or its wrapper may be the element that scrolls, so listen
    // on both; update is idempotent.
    scrollTargets = [list, list.parentElement].filter(Boolean);
    scrollTargets.forEach((el) => el.addEventListener('scroll', scheduleUpdate, { passive: true }));
    // Resize/layout moves the scroller; content changes (items added/removed, or
    // async children like icons rendering in and widening items) change what is
    // clipped. subtree catches the deep content changes the childList alone misses.
    resizeObserver = new ResizeObserver(refreshAndUpdate);
    resizeObserver.observe(list);
    mutationObserver = new MutationObserver(refreshAndUpdate);
    mutationObserver.observe(list, { childList: true, subtree: true });
    refreshAndUpdate();
    // Re-measure after the first paint, once layout (and any teleport) settles.
    measureRafId = requestAnimationFrame(() => {
      measureRafId = null;
      refreshAndUpdate();
    });
  }

  function detach() {
    scrollTargets.forEach((el) => el.removeEventListener('scroll', scheduleUpdate));
    scrollTargets = [];
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (measureRafId !== null) {
      cancelAnimationFrame(measureRafId);
      measureRafId = null;
    }
  }

  // Bind once the element exists, and re-bind if it is swapped (e.g. a v-if'd
  // list mounting later). immediate runs while the ref is still null (no-op).
  watch(
    listRef,
    (list) => {
      detach();
      if (list) attach(list);
    },
    { immediate: true }
  );

  onBeforeUnmount(detach);

  return { canScrollStart, canScrollEnd, fadeEdges, itemVisibility, scrollToNext, update };
};
